// src/components/common/Sidebar.jsx
import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Typography,
  Avatar,
  Chip,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Stack,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Dashboard,
  Inventory,
  ShoppingCart,
  People,
  Analytics,
  VerifiedUser,
  Category,
  Straighten,
  ColorLens,
  LocalOffer,
  AttachMoney,
  Badge as BadgeIcon,
  Palette as ThemeIcon,
  Chat as ChatIcon,
  Settings as SettingsIcon,
  LocalShipping, // ✅ NEW: Added Logistics Icon
} from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import { useSettings } from '../../context/SettingsContext';
import { useThemeContext } from '../../context/ThemeContext';
import { resolveImageUrl, getUserAvatarUrl } from '../../utils/imageUtils';

const drawerWidth = 260;

const Sidebar = ({ mobileOpen, onDrawerToggle, userAvatarUrl }) => {
  const { user } = useAuth();
  const { siteName, siteLogo } = useSettings();
  const { colors } = useThemeContext();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const isDark = colors?.themeMode === 'dark';
  const primaryColor = colors?.primaryColor || '#E31E24';
  const isAdmin = user?.role === 'admin';

  const siteLogoUrl = resolveImageUrl(siteLogo);
  const activeAvatarUrl = userAvatarUrl || getUserAvatarUrl(user);

  const getMenuItems = () => {
    const adminMenu = [
      { text: 'Dashboard', icon: <Dashboard />, path: '/admin/dashboard' },
      { text: 'Messages', icon: <ChatIcon />, path: '/admin/messages' },
      { text: 'Orders', icon: <ShoppingCart />, path: '/admin/orders' },
      { text: 'Users', icon: <People />, path: '/admin/users' },
      { text: 'Vendors', icon: <VerifiedUser />, path: '/admin/vendors' },
      { text: 'Products', icon: <Inventory />, path: '/admin/products' },
      { text: 'KYC Verification', icon: <BadgeIcon />, path: '/admin/kyc' },
      { text: 'Payouts', icon: <AttachMoney />, path: '/admin/payouts' },
      { text: 'Coupons', icon: <LocalOffer />, path: '/admin/coupons' },
      { text: 'Categories', icon: <Category />, path: '/admin/categories' },
      { text: 'Sizes', icon: <Straighten />, path: '/admin/sizes' },
      { text: 'Colors', icon: <ColorLens />, path: '/admin/colors' },
      { text: 'Badges', icon: <LocalOffer />, path: '/admin/badges' },
      { text: 'Logistics', icon: <LocalShipping />, path: '/admin/logistics' }, // ✅ NEW: Added Logistics item
    ];

    const vendorMenu = [
      { text: 'Dashboard', icon: <Dashboard />, path: '/vendor/dashboard' },
      { text: 'Messages', icon: <ChatIcon />, path: '/vendor/chat' },
      { text: 'Products', icon: <Inventory />, path: '/vendor/products' },
      { text: 'Orders', icon: <ShoppingCart />, path: '/vendor/orders' },
      { text: 'Customers', icon: <People />, path: '/vendor/customers' },
      { text: 'Analytics', icon: <Analytics />, path: '/vendor/analytics' },
      { text: 'Payouts', icon: <AttachMoney />, path: '/vendor/payouts' },
      { text: 'KYC', icon: <BadgeIcon />, path: '/vendor/kyc' },
      { text: 'Coupons', icon: <LocalOffer />, path: '/vendor/coupons' },
    ];

    return isAdmin ? adminMenu : vendorMenu;
  };

  const menuItems = getMenuItems();
  const bottomSettingPath = isAdmin ? '/admin/theme-settings' : '/vendor/settings';
  const bottomSettingLabel = isAdmin ? 'Theme Settings' : 'Settings';
  const bottomSettingIcon = isAdmin ? <ThemeIcon fontSize="small" /> : <SettingsIcon fontSize="small" />;

  const getAvatarLetter = () => {
    if (user?.name) return user.name[0].toUpperCase();
    if (user?.storeName) return user.storeName[0].toUpperCase();
    return 'U';
  };

  const drawer = (
    <Box
      sx={{
        height: '100%',
        maxHeight: '100%',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: 'background.paper',
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          pt: { xs: 2, sm: 2.5 },
          pb: { xs: 1.5, sm: 2 },
          px: 2,
          bgcolor: 'background.paper',
          color: 'text.primary',
          textAlign: 'center',
          borderBottom: `2px solid ${primaryColor}`,
          flexShrink: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {siteLogoUrl ? (
          <Box
            sx={{
              width: { xs: 45, sm: 55, md: 65 },
              height: { xs: 35, sm: 45, md: 55 },
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'transform 0.3s ease',
              '&:hover': { transform: 'scale(1.04)' },
            }}
          >
            <img
              src={siteLogoUrl}
              alt={siteName || 'Logo'}
              style={{
                maxWidth: '100%',
                maxHeight: '100%',
                objectFit: 'contain',
                display: 'block',
              }}
            />
          </Box>
        ) : (
          <Avatar
            sx={{
              width: { xs: 42, sm: 48, md: 54 },
              height: { xs: 42, sm: 48, md: 54 },
              bgcolor: primaryColor,
              color: 'white',
              fontSize: { xs: 18, sm: 22, md: 24 },
              fontWeight: 800,
              boxShadow: `0 4px 14px ${primaryColor}40`,
            }}
          >
            {siteName ? siteName.charAt(0) : 'V'}
          </Avatar>
        )}

        <Typography
          variant="subtitle1"
          fontWeight="800"
          sx={{
            fontSize: { xs: '0.85rem', sm: '0.95rem' },
            mt: 0.8,
            color: 'text.primary',
            letterSpacing: '0.2px',
          }}
        >
          {siteName}
        </Typography>
      </Box>

      <Box
        sx={{
          px: 2,
          py: 1.5,
          bgcolor: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)',
          borderBottom: '1px solid',
          borderColor: 'divider',
          flexShrink: 0,
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Avatar
            src={activeAvatarUrl}
            sx={{
              width: { xs: 34, sm: 38 },
              height: { xs: 34, sm: 38 },
              bgcolor: activeAvatarUrl ? 'transparent' : primaryColor,
              fontSize: { xs: 12, sm: 14 },
              fontWeight: 700,
              boxShadow: activeAvatarUrl ? 'none' : `0 2px 8px ${primaryColor}35`,
              border: activeAvatarUrl ? '2px solid' : 'none',
              borderColor: 'divider',
            }}
          >
            {!activeAvatarUrl && getAvatarLetter()}
          </Avatar>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              variant="body2"
              fontWeight="700"
              sx={{
                fontSize: { xs: '0.78rem', sm: '0.83rem' },
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                color: 'text.primary',
              }}
            >
              {user?.name || user?.storeName || 'User'}
            </Typography>
            <Chip
              label={isAdmin ? 'Admin Portal' : 'Vendor Portal'}
              size="small"
              sx={{
                height: 18,
                fontSize: '0.6rem',
                bgcolor: isAdmin ? 'rgba(255, 107, 107, 0.15)' : 'rgba(78, 205, 196, 0.15)',
                color: isAdmin ? '#FF6B6B' : '#2CB5A8',
                fontWeight: 800,
                borderRadius: '6px',
                mt: 0.2,
              }}
            />
          </Box>
        </Stack>
      </Box>

      <Box
        sx={{
          flex: '1 1 auto',
          minHeight: 0,
          overflowY: 'auto',
          px: 1.5,
          py: 1.5,
          '&::-webkit-scrollbar': {
            width: 5,
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: isDark ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.15)',
            borderRadius: 4,
          },
        }}
      >
        <List disablePadding>
          {menuItems.map((item) => {
            const isSelected =
              location.pathname === item.path ||
              location.pathname.startsWith(item.path + '/');

            return (
              <ListItem
                button
                key={item.text}
                onClick={() => {
                  navigate(item.path);
                  if (isMobile) onDrawerToggle();
                }}
                sx={{
                  mb: 0.6,
                  borderRadius: '10px',
                  bgcolor: isSelected ? primaryColor : 'transparent',
                  color: isSelected ? 'white' : 'text.primary',
                  minHeight: 42,
                  px: 1.8,
                  transition: 'all 0.22s ease-in-out',
                  boxShadow: isSelected ? `0 4px 12px ${primaryColor}40` : 'none',
                  '&:hover': {
                    bgcolor: isSelected
                      ? primaryColor
                      : isDark
                      ? 'rgba(255,255,255,0.08)'
                      : 'rgba(0,0,0,0.04)',
                    transform: 'translateX(4px)',
                  },
                }}
              >
                <ListItemIcon
                  sx={{
                    color: isSelected ? 'white' : 'inherit',
                    minWidth: 36,
                    '& .MuiSvgIcon-root': {
                      fontSize: 20,
                    },
                  }}
                >
                  {item.icon}
                </ListItemIcon>
                <ListItemText
                  primary={item.text}
                  primaryTypographyProps={{
                    fontWeight: isSelected ? 700 : 500,
                    fontSize: '0.85rem',
                    noWrap: true,
                  }}
                />
              </ListItem>
            );
          })}
        </List>
      </Box>

      <Box
        sx={{
          borderTop: '1px solid',
          borderColor: 'divider',
          p: 1.5,
          pb: { xs: 2.5, sm: 1.5 },
          flexShrink: 0,
          bgcolor: isDark ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.01)',
        }}
      >
        <ListItem
          button
          onClick={() => {
            navigate(bottomSettingPath);
            if (isMobile) onDrawerToggle();
          }}
          sx={{
            borderRadius: '10px',
            minHeight: 40,
            px: 1.5,
            bgcolor: location.pathname.includes(bottomSettingPath)
              ? isDark
                ? 'rgba(255,255,255,0.12)'
                : 'rgba(0,0,0,0.06)'
              : 'transparent',
            '&:hover': {
              bgcolor: isDark
                ? 'rgba(255,255,255,0.08)'
                : 'rgba(0,0,0,0.04)',
              transform: 'translateX(3px)',
            },
            transition: 'all 0.2s ease',
          }}
        >
          <ListItemIcon sx={{ minWidth: 34 }}>
            {bottomSettingIcon}
          </ListItemIcon>
          <ListItemText
            primary={bottomSettingLabel}
            primaryTypographyProps={{
              fontSize: '0.82rem',
              fontWeight: 600,
              color: 'text.secondary',
            }}
          />
        </ListItem>
      </Box>
    </Box>
  );

  return (
    <>
      <Box
        component="nav"
        sx={{
          width: { sm: drawerWidth },
          flexShrink: { sm: 0 },
          display: { xs: 'none', sm: 'block' },
        }}
      >
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              borderRight: '1px solid',
              borderColor: 'divider',
              bgcolor: 'background.paper',
              boxShadow: isDark
                ? '4px 0 24px rgba(0,0,0,0.25)'
                : '4px 0 24px rgba(0,0,0,0.04)',
              zIndex: (theme) => theme.zIndex.drawer,
              top: { sm: '64px' },
              height: 'calc(100vh - 64px)',
              overflow: 'hidden',
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>

      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={onDrawerToggle}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: 'block', sm: 'none' },
          '& .MuiDrawer-paper': {
            boxSizing: 'border-box',
            width: drawerWidth,
            bgcolor: 'background.paper',
            height: '100%',
            maxHeight: '100%',
            boxShadow: '8px 0 32px rgba(0,0,0,0.2)',
            zIndex: (theme) => theme.zIndex.drawer + 2,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
          },
        }}
      >
        {drawer}
      </Drawer>
    </>
  );
};

export default Sidebar;